import { useMemo } from "react";
import ConfirmModal from "../confirm-modal/confirm-modal";
import SelectProductsFilter from "./select-products-filter";
import { initialFilterState } from "./select-products-filter-state";
import { productsColumn, stationProductsColumn } from "./select-products-columns";
import { mapDataToList, mapFilterToRequest } from "./select-products-mapper";
import { getCategoryApi, getProductsApi } from "apis";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import locale from "localization";
import styles from "./select-products-modal.module.scss";
import classNames from "classnames";
import { excludeFilterAutoComplete } from "utils";
import { prettifyProductType } from "utils/pretty.utils";
import useSelectableItemsModal from "hooks/useSelectableItems";
import { useApi, useMountAsync } from "hooks";

const SelectProductsModal = ({
  onFetch = (allProducts) => 0,
  allProducts = [],
  fromStation = false,
  coloredProduct = true,
  excludeProductType = [],
  value,
  onChange,
  excludeCols = [],
  showFilter = true,
  className,
  dataTableProps = {},
  filterProps = {},
  ...modal
}) => {
  const uniqueProductTypes = useMemo(() => {
    const all = allProducts || [];
    const options = all.reduce((acc, { category, type }) => {
      if (!acc.some((p) => p.value === type)) {
        acc.push({ label: prettifyProductType(type), value: type });
      }
      return acc;
    }, []);

    return {
      options,
      filtered: options.filter(({ value }) => !excludeProductType?.includes(value)),
    };
  }, [allProducts, excludeProductType]);

  const variants = useMemo(() => [...new Set(allProducts?.map((p) => p.variant))], [allProducts]);
  const { request: getCategory, result: resCategores } = useApi({
    api: getCategoryApi,
    params: {
      perPage: 1000,
      page: 1,
    },
  });

  const categoriesOptions = useMemo(() => {
    const acceptedIds = [1, 2, 3, 4, 5, 9]; // PRD.
    return resCategores?.categories
      ?.filter((category) => acceptedIds.includes(category.categoryId))
      .map((category) => ({
        label: category.name,
        value: String(category.categoryId),
      }));
  }, [resCategores]);

  const initialFilterStateRes = useMemo(() => {
    return {
      ...initialFilterState(),
      productType: {
        isSelectedAll: !excludeProductType || excludeProductType?.length === 0,
        value: uniqueProductTypes.filtered.map(({ value }) => value),
      },
      variants: {
        isSelectedAll: true,
        value: variants,
      },
      categoryIds: {
        isSelectedAll: true,
        value: categoriesOptions?.map((opt) => opt.value),
      },
    };
  }, [categoriesOptions, excludeProductType, uniqueProductTypes.filtered, variants]);

  const dataTable = {
    api: {
      api: getProductsApi,
      mapper: (param) => param,
    },
    filter: {
      initialState: initialFilterStateRes,
      mapper: mapFilterToRequest,
    },
    table: {
      key: "products",
      mapper: (product, { onCheck, selected }) =>
        mapDataToList({
          product,
          selected: selected,
          coloredProduct: coloredProduct,
          onCheck: onCheck,
        }),
      columns: fromStation
        ? stationProductsColumn
        : productsColumn.filter((col) => !excludeCols.includes(col.key)),
      isReverse: true,
    },
  };

  const {
    selected,
    setSelected,
    handleOnCheckAll,
    setIsCheckAllState,
    isCheckAllState,
    allItemsState,
    dataTable: { table, filter, search },
  } = useSelectableItemsModal({
    active: modal?.active,
    uniqueKey: "sku",
    allItems: allProducts,
    value: value,
    dataTable,
    onFetchAllItemMapFilterState: (filterState) => {
      return {
        ...filterState,
        productType: excludeFilterAutoComplete({
          ...filterState.productType,
          exclude: excludeProductType,
          allOptions: uniqueProductTypes.options,
        }),
      };
    },
  });

  const isMoreThan50Selected = selected?.length > 50;

  const promoSearchFilter = [locale.sku, locale.productName].join(", ");
  const stationSearchFilter = locale.productName;

  const onClickSave = () => {
    const allItems = allProducts.length > 0 ? allProducts : allItemsState;
    onChange?.(
      selected
        .map((name) => allItems.find(({ sku }) => sku === name))
        .filter((product) => !!product)
    );
  };

  useMountAsync(async () => {
    if (!filterProps?.hiddenInputs?.category) {
      await getCategory();
    }
  });

  return (
    <ConfirmModal
      {...modal}
      className={classNames(styles.modal, {
        [`${styles.station}`]: fromStation,
        [`${styles.promos}`]: !fromStation,
        [className]: true,
      })}
      primary={{
        ...modal?.primary,
        text: modal?.primary?.text || locale.save,
        disabled:
          isMoreThan50Selected ||
          (modal?.required ? selected.length === 0 : value?.length === 0 && selected.length === 0),
        onClick: onClickSave,
      }}
      secondary={{
        ...modal?.secondary,
        text: locale.cancel,
        onClick: () => {
          filter?.resetFilter?.(initialFilterStateRes);
        },
      }}
    >
      <SelectProductsFilter
        {...filterProps}
        {...filter}
        {...search}
        allProducts={allProducts}
        showFilter={showFilter}
        actionText={<locale.Populate text={locale.nSelected} items={[selected.length]} />}
        actionProps={{
          link: selected.length > 0,
          variant: "text",
          style: {
            opacity: selected.length === 0 && 0.5,
          },
        }}
        actionOnClick={() => {
          setSelected([]);
          setIsCheckAllState(false);
        }}
        uniqueProductTypes={uniqueProductTypes.filtered}
        placeholder={fromStation ? stationSearchFilter : promoSearchFilter}
        categoriesOptions={categoriesOptions}
        initialFilterState={initialFilterStateRes}
      />
      <DataTableV2
        {...table}
        onCheckAll={handleOnCheckAll}
        actionHeaderProps={{
          checked: isCheckAllState,
        }}
        size="small"
        {...dataTableProps}
      />
    </ConfirmModal>
  );
};

export default SelectProductsModal;
